import React, { useState, useEffect, useRef } from "react";
import { Activity, Calendar, Settings, CheckCircle, TrendingUp, Clock, Shield, Download, Star, Menu, X, ChevronRight, Users, DollarSign, BarChart2, Zap } from "lucide-react";

const APP_URL = "";

// ── Lightbox Context ─────────────────────────────────────────────────────────
const LightboxContext = React.createContext(null);

// ── Logo Component ──────────────────────────────────────────────────────────
const RVULogo = ({ size = 36, textColor = "#0f172a" }) => (
  <div className="flex items-center gap-2.5">
    <img
      src="/rvu-logo.png"
      alt="Doc RVU Tracker Logo"
      style={{
        width: size,
        height: size,
        borderRadius: Math.round(size * 0.25) + "px",
        flexShrink: 0,
        objectFit: "cover",
        boxShadow: "0 2px 8px rgba(22,163,74,0.30)",
      }}
    />
    <span
      style={{
        fontSize: size * 0.47,
        fontWeight: 700,
        color: textColor,
        letterSpacing: "-0.5px",
        fontFamily: "Inter, sans-serif",
      }}
    >
      Doc RVU Tracker
    </span>
  </div>
);

// ── Animated counter ─────────────────────────────────────────────────────────
const AnimatedCounter = ({ end, suffix = "", prefix = "", duration = 2000 }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started.current) {
          started.current = true;
          const startTime = Date.now();
          const update = () => {
            const elapsed = Date.now() - startTime;
            const progress = Math.min(elapsed / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 3);
            setCount(Math.floor(eased * end));
            if (progress < 1) requestAnimationFrame(update);
          };
          requestAnimationFrame(update);
        }
      },
      { threshold: 0.5 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [end, duration]);

  return <span ref={ref}>{prefix}{count.toLocaleString()}{suffix}</span>;
};

// ── Section fade-in hook ─────────────────────────────────────────────────────
const useFadeIn = (threshold = 0.12) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setVisible(true); },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
};

const FadeIn = ({ children, delay = 0, className = "" }) => {
  const [ref, visible] = useFadeIn();
  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(28px)",
        transition: `opacity 0.65s ease ${delay}ms, transform 0.65s ease ${delay}ms`,
      }}
    >
      {children}
    </div>
  );
};

// ── App Screenshot Frame ─────────────────────────────────────────────────────
const AppFrame = ({ src, alt, className = "" }) => {
  const openLightbox = React.useContext(LightboxContext);
  return (
    <div
      className={`relative rounded-2xl overflow-hidden shadow-2xl border border-slate-200 cursor-zoom-in group ${className}`}
      style={{ boxShadow: "0 25px 60px rgba(15,22,35,0.20), 0 0 0 1px rgba(255,255,255,0.08)" }}
      onClick={() => openLightbox && openLightbox(src, alt)}
    >
      <img src={src} alt={alt} className="w-full block transition-transform duration-300 group-hover:scale-[1.02]" loading="lazy" />
      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300 flex items-center justify-center">
        <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-white/90 rounded-full p-2 shadow-lg">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#0f172a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/><line x1="11" y1="8" x2="11" y2="14"/><line x1="8" y1="11" x2="14" y2="11"/></svg>
        </div>
      </div>
    </div>
  );
};

// ── Lightbox Modal ────────────────────────────────────────────────────────────
const Lightbox = ({ src, alt, onClose }) => {
  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", handler);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-8"
      style={{ background: "rgba(0,0,0,0.85)", backdropFilter: "blur(6px)" }}
      onClick={onClose}
    >
      <div
        className="relative max-w-5xl w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute -top-12 right-0 text-white/80 hover:text-white transition-colors flex items-center gap-2 text-sm font-medium"
        >
          <X size={20} /> Close
        </button>
        <div className="rounded-2xl overflow-hidden shadow-2xl border border-white/10">
          <img src={src} alt={alt} className="w-full block" />
        </div>
      </div>
    </div>
  );
};

// ── Feature Card ─────────────────────────────────────────────────────────────
const FeatureCard = ({ icon: Icon, title, description, color = "blue", delay = 0 }) => {
  const colorMap = {
    blue: "bg-blue-50 text-blue-600",
    teal: "bg-teal-50 text-teal-600",
    green: "bg-green-50 text-green-600",
    amber: "bg-amber-50 text-amber-600",
    purple: "bg-purple-50 text-purple-600",
  };
  return (
    <FadeIn delay={delay}>
      <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm hover:shadow-md hover:border-blue-100 transition-all duration-300 hover:-translate-y-0.5 h-full">
        <div className={`w-10 h-10 rounded-xl ${colorMap[color]} flex items-center justify-center mb-4`}>
          <Icon size={20} />
        </div>
        <h3 className="font-semibold text-slate-900 mb-2 text-base">{title}</h3>
        <p className="text-slate-500 text-sm leading-relaxed">{description}</p>
      </div>
    </FadeIn>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
//  MAIN LANDING PAGE
// ─────────────────────────────────────────────────────────────────────────────
export default function LandingPage() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    setMenuOpen(false);
  };

  return (
    <LightboxContext.Provider value={(src, alt) => setLightbox({ src, alt })}>
    <div className="min-h-screen font-sans antialiased" style={{ fontFamily: "Inter, -apple-system, BlinkMacSystemFont, sans-serif", background: "#f8fafc" }}>

      {/* ══ NAVBAR ══════════════════════════════════════════════════════════ */}
      <nav
        data-testid="navbar"
        className="fixed top-0 left-0 right-0 z-50 transition-all duration-300"
        style={{
          background: scrolled ? "rgba(255,255,255,0.96)" : "rgba(255,255,255,0.85)",
          backdropFilter: "blur(16px)",
          borderBottom: scrolled ? "1px solid #e2e8f0" : "1px solid transparent",
          boxShadow: scrolled ? "0 1px 20px rgba(0,0,0,0.06)" : "none",
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <a href="/" data-testid="nav-logo"><RVULogo size={42} /></a>

            {/* Desktop nav */}
            <div className="hidden md:flex items-center gap-8">
              {[["Features", "features"], ["How It Works", "how-it-works"], ["Pricing", "pricing"]].map(([label, id]) => (
                <button
                  key={id}
                  onClick={() => scrollTo(id)}
                  className="text-base font-medium text-slate-600 hover:text-slate-900 transition-colors"
                  data-testid={`nav-${id}`}
                >
                  {label}
                </button>
              ))}
              <a
                href="/rvu-articles"
                className="text-base font-medium text-slate-600 hover:text-slate-900 transition-colors"
                data-testid="nav-articles"
              >
                Articles
              </a>
              <a
                href="/free-rvu-calculator"
                className="text-xs font-semibold text-blue-600 bg-blue-50 hover:bg-blue-100 px-3 py-1.5 rounded-full border border-blue-200 transition-colors"
              >
                Free wRVU Calculator
              </a>
            </div>

            <div className="hidden md:flex items-center gap-4">
              <a
                href={`${APP_URL}/login`}
                
                
                data-testid="nav-login-btn"
                className="text-base font-medium text-slate-700 hover:text-blue-600 transition-colors px-4 py-2"
              >
                Log In
              </a>
              <a
                href={`${APP_URL}/register`}
                
                
                data-testid="nav-register-btn"
                className="text-base font-semibold text-white rounded-full px-6 py-2.5 transition-all duration-200 hover:-translate-y-px hover:shadow-lg"
                style={{ background: "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)", boxShadow: "0 2px 12px rgba(37,99,235,0.30)" }}
              >
                Get Started for Free
              </a>
            </div>

            {/* Mobile menu button */}
            <button
              className="md:hidden p-2 rounded-lg text-slate-600 hover:bg-slate-100"
              onClick={() => setMenuOpen(!menuOpen)}
              data-testid="mobile-menu-btn"
            >
              {menuOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {menuOpen && (
          <div className="md:hidden bg-white border-t border-slate-100 px-4 py-4 space-y-3">
            {[["Features", "features"], ["How It Works", "how-it-works"], ["Pricing", "pricing"]].map(([label, id]) => (
              <button key={id} onClick={() => scrollTo(id)} className="block w-full text-left text-sm font-medium text-slate-700 py-2">
                {label}
              </button>
            ))}
            <a href="/rvu-articles" className="block text-sm font-medium text-slate-700 py-2">Articles</a>
            <div className="pt-3 border-t border-slate-100 flex flex-col gap-2">
              <a href={`${APP_URL}/login`}   className="text-center py-2 text-sm font-medium text-slate-700 border border-slate-200 rounded-full">
                Log In
              </a>
              <a href={`${APP_URL}/register`}   className="text-center py-2.5 text-sm font-semibold text-white rounded-full" style={{ background: "linear-gradient(135deg, #2563eb, #1d4ed8)" }}>
                Get Started Free
              </a>
            </div>
          </div>
        )}
      </nav>

      {/* ══ HERO ════════════════════════════════════════════════════════════ */}
      <section className="pt-28 pb-16 md:pt-36 md:pb-24 px-4 overflow-hidden" style={{ background: "linear-gradient(160deg, #f0f7ff 0%, #f8fafc 50%, #f0fdf4 100%)" }}>
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-16">

            {/* Left: Copy */}
            <div className="flex-1 text-center lg:text-left">
              <div
                className="inline-flex items-center gap-2 bg-blue-50 border border-blue-100 text-blue-700 text-xs font-semibold px-4 py-1.5 rounded-full mb-6"
                data-testid="hero-badge"
                style={{ animation: "fadeDown 0.6s ease both" }}
              >
                <Activity size={12} />
                Built for Urgent Care · ER · Primary Care
              </div>

              <h2
                className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 mb-4 leading-[1.08]"
                data-testid="hero-headline"
                style={{ animation: "fadeUp 0.7s ease 0.1s both" }}
              >
                Know Your Worth.{" "}
                <span style={{ background: "linear-gradient(135deg, #2563eb 0%, #0ea5e9 100%)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
                  One Click
                </span>{" "}
                at a Time.
              </h2>

              <h1
                className="text-2xl sm:text-3xl font-bold text-slate-800 mb-6 tracking-tight"
                data-testid="hero-slogan"
                style={{ animation: "fadeUp 0.7s ease 0.15s both" }}
              >
                The Fastest RVU Tracker available.{" "}
                <span style={{ background: "linear-gradient(135deg, #2563eb 0%, #0ea5e9 100%)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", textDecoration: "underline", textDecorationColor: "#2563eb", textDecorationThickness: "3px", textUnderlineOffset: "5px" }}>
                  PERIOD.
                </span>
              </h1>

              <p
                className="text-lg text-slate-600 mb-8 leading-relaxed max-w-xl mx-auto lg:mx-0"
                data-testid="hero-subtext"
                style={{ animation: "fadeUp 0.7s ease 0.2s both" }}
              >
                The easiest way for providers to track patient visits, 
                calculate earnings, and monitor their productivity — right from your browser.
              </p>

              <div
                className="flex flex-col sm:flex-row gap-3 justify-center lg:justify-start"
                style={{ animation: "fadeUp 0.7s ease 0.3s both" }}
              >
                <a
                  href={`${APP_URL}/register`}
                  
                  
                  data-testid="hero-cta-primary"
                  className="flex items-center justify-center gap-2 text-white font-semibold rounded-full px-8 py-3.5 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-xl"
                  style={{ background: "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)", boxShadow: "0 4px 20px rgba(37,99,235,0.30)" }}
                >
                  Start Tracking for Free
                  <ChevronRight size={18} />
                </a>
                <a
                  href={`${APP_URL}/login`}
                  
                  
                  data-testid="hero-cta-secondary"
                  className="flex items-center justify-center gap-2 text-slate-700 font-semibold rounded-full px-8 py-3.5 bg-white border border-slate-200 hover:bg-slate-50 hover:border-slate-300 transition-all duration-200"
                >
                  Log In to My Account
                </a>
              </div>
              <p className="mt-4 text-xs text-slate-400" style={{ animation: "fadeUp 0.7s ease 0.4s both" }}>
                Cancel anytime · Only $9.99/month
              </p>
            </div>

            {/* Right: App screenshot */}
            <div className="flex-1 w-full max-w-2xl lg:max-w-none" style={{ animation: "fadeUp 0.8s ease 0.3s both" }}>
              <AppFrame
                src="/screenshots/hero-screenshot.png"
                alt="Doc RVU Tracker - Shift Tracker Dashboard"
                className="w-full"
              />
              {/* Subtitle under screenshot */}
              <div
                className="mt-4 rounded-2xl px-6 py-4 text-center"
                style={{ background: "rgba(255,255,255,0.85)", border: "1px solid #e2e8f0", boxShadow: "0 4px 16px rgba(15,23,42,0.07)" }}
              >
                <p className="text-base font-extrabold text-slate-800 mb-1.5">
                  Built to use &nbsp;
                  <span style={{ background: "linear-gradient(135deg, #2563eb 0%, #0ea5e9 100%)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
                    During your Shift
                  </span>
                </p>
                <p className="text-sm text-slate-600 leading-relaxed">
                  Log visits in <span className="font-semibold text-slate-700 underline underline-offset-2">One Click</span> — no more worrying about the money while you work.
                </p>
                <p className="text-sm text-slate-600 leading-relaxed">
                  Get back to what matters most — Patient Care.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══ SOCIAL PROOF STRIP ══════════════════════════════════════════════ */}
      <section className="bg-white border-y border-slate-100 py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-10 text-center">
            {[
              { icon: Zap,        label: <><span>1 click to</span><br /><span>Log a Visit</span></>,                                    color: "#f59e0b" },
              { icon: Activity,   label: <><span>Tracks RVUs from</span><br /><span>Visits and Procedures</span></>,                       color: "#14b8a6" },
              { icon: DollarSign, label: <><span>Track Earnings</span><br /><span>in Real Time</span></>,                                  color: "#22c55e" },
              { icon: Calendar,   label: <><span>Calendar View with</span><br /><span>Monthly Earnings and RVU Totals</span></>,            color: "#3b82f6" },
            ].map(({ icon: Icon, label, color }) => (
              <FadeIn key={color}>
                <div className="flex flex-col items-center gap-4">
                  <div className="w-20 h-20 rounded-3xl flex items-center justify-center" style={{ background: `${color}18` }}>
                    <Icon size={40} style={{ color }} />
                  </div>
                  <div className="text-base font-bold text-slate-800 leading-snug">{label}</div>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ══ FEATURES ════════════════════════════════════════════════════════ */}
      <section id="features" className="py-20 md:py-28 px-4">
        <div className="max-w-7xl mx-auto">

          {/* Section header */}
          <FadeIn>
            <div className="text-center mb-16">
              <span className="text-blue-600 text-sm font-semibold uppercase tracking-widest">Features</span>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mt-2 mb-4 tracking-tight">
                Everything You Need, Nothing You Don't
              </h2>
              <p className="text-slate-500 text-lg max-w-2xl mx-auto leading-relaxed">
                Designed specifically for busy providers — simple enough to use between patients, 
                powerful enough to replace your manual spreadsheets.
              </p>
            </div>
          </FadeIn>

          {/* ─── Feature 1: Quick-Add Visits ──────────────────────────────── */}
          <div className="flex flex-col lg:flex-row items-center gap-12 mb-24">
            <FadeIn className="flex-1 lg:order-2">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center">
                  <Zap size={20} />
                </div>
                <span className="text-blue-600 text-sm font-semibold uppercase tracking-widest">Quick-Add Visits</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-5 tracking-tight leading-tight">
                Track a Patient Visit<br />in Under a Second
              </h2>
              <p className="text-slate-600 text-lg leading-relaxed mb-6">
                Your 6 most common CPT codes are always one tap away. Hit <strong className="text-slate-800">"+"</strong> and 
                your wRVUs and earnings update instantly. No typing, no searching — just fast, 
                accurate tracking while you see the next patient.
              </p>
              <ul className="space-y-3">
                {[
                  "Customizable Quick-Add Visits for Your Practice",
                  "Perfect for acute visit and procedural codes for Urgent Care & ER",
                  "Perfect for preventative and follow-up visit codes for Primary Care",
                  "Built-in CPT lookup tool to quickly and accurately find codes and their wRVU values",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3 text-slate-600 text-base">
                    <CheckCircle size={16} className="mt-0.5 text-green-500 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </FadeIn>
            <FadeIn className="flex-1 lg:order-1" delay={150}>
              <div className="flex flex-col gap-4">
                <div className="flex flex-row gap-4">
                  <AppFrame src="/screenshots/qav-er-new.png" alt="Quick-Add Visits - ER" className="flex-1 min-w-0" />
                  <AppFrame src="/screenshots/qav-primary-new.png" alt="Quick-Add Visits - Primary Care" className="flex-1 min-w-0" />
                </div>
                <div className="flex justify-center">
                  <div className="w-1/2">
                    <AppFrame src="/screenshots/qav-hospital.png" alt="Quick-Add Visits - Hospital Medicine" />
                  </div>
                </div>
              </div>
            </FadeIn>
          </div>

          {/* ─── Feature 1b: Other Visits, Procedures & Hourly ──────────────── */}
          <div className="flex flex-col lg:flex-row items-center gap-12 mb-24">
            <FadeIn className="flex-1">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-green-50 text-green-600 flex items-center justify-center">
                  <TrendingUp size={20} />
                </div>
                <span className="text-green-600 text-sm font-semibold uppercase tracking-widest">Track Every Dollar</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-5 tracking-tight leading-tight">
                Have Visits or Procedures<br />Beyond Your Quick-Adds?
              </h2>
              <p className="text-slate-600 text-lg leading-relaxed mb-6">
                Manually add patient visits and procedures just as fast with the<br /><strong className="text-slate-800">CPT Autofill Feature</strong>. Build your <strong className="text-slate-800">Quick-Add CPT List</strong> to save your most-used visit and procedure codes for quick-add tracking. Add <strong className="text-slate-800">Hourly Earnings</strong> for moonlighting shifts or mixed compensation models — so every dollar you earn is tracked.
              </p>
              <ul className="space-y-3">
                {[
                  "Log CPT visit codes not in your Quick-Add Visits panel quickly",
                  "Track procedures and ancillary services separately in its own section",
                  "Add hourly earnings for shifts with an hourly rate",
                  "Build & customize your CPT List in the collapsible tab",
                  "Quick-Add CPT list and all sections feature CPT Autofill for fast, accurate entry",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3 text-slate-600 text-base">
                    <CheckCircle size={16} className="mt-0.5 text-green-500 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </FadeIn>
            <FadeIn className="flex-1" delay={150}>
              <AppFrame src="/screenshots/track-every-dollar.png" alt="Other Patient Visits, Procedures & Services" />
            </FadeIn>
          </div>

          {/* ─── Feature 2: Real-Time Earnings ────────────────────────────── */}
          <div className="flex flex-col lg:flex-row items-center gap-12 mb-24">
            <FadeIn className="flex-1 lg:order-2">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-teal-50 text-teal-600 flex items-center justify-center">
                  <DollarSign size={20} />
                </div>
                <span className="text-teal-600 text-sm font-semibold uppercase tracking-widest">Real-Time Earnings</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-5 tracking-tight leading-tight">
                See Your Earnings<br />Update in Real Time
              </h2>
              <p className="text-slate-600 text-lg leading-relaxed mb-6">
                A persistent status bar at the bottom of your tracker shows your live running totals: 
                Patient Count, Total wRVUs, pre-tax & post-tax Dollars — No more waiting for your paycheck to know <span className="whitespace-nowrap">how much you made.</span>
              </p>
              <ul className="space-y-3">
                {[
                  "Instant wRVU calculations per CPT code",
                  "Pre-tax and post-tax earnings shown side by side",
                  "Set your own $/wRVU rate and tax bracket in Settings",
                  "Shift Totals update with each tap",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3 text-slate-600 text-base">
                    <CheckCircle size={16} className="mt-0.5 text-green-500 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </FadeIn>
            <FadeIn className="flex-1 lg:order-1" delay={150}>
              <AppFrame src="/screenshots/realtime-earnings.png" alt="Real-Time Earnings Tracker" />
            </FadeIn>
          </div>

          {/* ─── Feature 3: Calendar Overview ─────────────────────────────── */}
          <div className="flex flex-col lg:flex-row items-center gap-12 mb-24">
            <FadeIn className="flex-1">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-purple-50 text-purple-600 flex items-center justify-center">
                  <Calendar size={20} />
                </div>
                <span className="text-purple-600 text-sm font-semibold uppercase tracking-widest">Calendar View</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-5 tracking-tight leading-tight">
                Your Entire Month<br />at a Glance
              </h2>
              <p className="text-slate-600 text-lg leading-relaxed mb-6">
                The Calendar tab displays every shift with its patient count, wRVU total, 
                and earnings right on the calendar day. Monthly totals are summarized at the top 
                — so you always know where you stand.
              </p>
              <ul className="space-y-3">
                {[
                  "Visual month-by-month shift history",
                  "Daily patient count & wRVUs displayed on each date",
                  "Monthly summary totals that update as you work",
                  "Click any date to view or edit that shift",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3 text-slate-600 text-base">
                    <CheckCircle size={16} className="mt-0.5 text-green-500 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </FadeIn>
            <FadeIn className="flex-1" delay={150}>
              <AppFrame src="/screenshots/calendar-view.png" alt="Monthly Calendar Overview" />
            </FadeIn>
          </div>

          {/* ─── Feature 4: Settings ──────────────────────────────────────── */}
          <div className="flex flex-col lg:flex-row items-center gap-12 mb-8">
            <FadeIn className="flex-1 lg:order-2">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center">
                  <Settings size={20} />
                </div>
                <span className="text-amber-600 text-sm font-semibold uppercase tracking-widest">Customizable Settings</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-5 tracking-tight leading-tight">
                Tailored to<br />Your Practice
              </h2>
              <p className="text-slate-600 text-lg leading-relaxed mb-6">
                Set your own tax rate, $/wRVU conversion rate, and clinic locations. 
                Customize which CPT codes appear in your Quick-Add panel. 
                Print or Export your full shift history as a file for paycheck verification or personal records and accounting.
              </p>
              <ul className="space-y-3">
                {[
                  "Set your personal $/wRVU conversion rate",
                  "Custom tax rate for accurate post-tax estimates",
                  "Save multiple clinic/hospital locations",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3 text-slate-600 text-base">
                    <CheckCircle size={16} className="mt-0.5 text-green-500 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </FadeIn>
            <FadeIn className="flex-1 lg:order-1" delay={150}>
              <div className="flex flex-col gap-4">
                <AppFrame src="/screenshots/settings-new.png" alt="Settings - Tax Rate and wRVU Rate" />
                <AppFrame src="/screenshots/settings-locations.png" alt="Settings - Clinic Locations and Quick-Add Visit Types" />
              </div>
            </FadeIn>
          </div>

        </div>
      </section>

      {/* ══ MORE FEATURES GRID ══════════════════════════════════════════════ */}
      <section className="py-16 md:py-20 px-4 bg-white border-y border-slate-100">
        <div className="max-w-7xl mx-auto">
          <FadeIn>
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight mb-3">
                Even More Built-In Tools
              </h2>
              <p className="text-slate-500 max-w-xl mx-auto">All included in your subscription — no extra fees, no add-ons.</p>
            </div>
          </FadeIn>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <FeatureCard icon={Activity} color="teal" title="CPT Search Tool" description={<>A built-in search bar lets you look up any CPT code for quick reference — find codes, check wRVU values, and stay accurate on the fly.<br /><br />And yes, this has CPT Autofill as well!</>} delay={0} />
            <FeatureCard icon={BarChart2} color="blue" title="Shift History" description="Browse a chronological list of all your saved shifts with patients, wRVUs, and earnings. Navigate shift-by-shift through your history." delay={80} />
            <FeatureCard icon={Download} color="green" title="Shift Data Export" description="Export your complete shift history with date, location, CPT codes, patients, wRVUs, and earnings to an Excel-compatible file for your records and accounting." delay={160} />
            <FeatureCard icon={Shield} color="blue" title="Private & Secure" description="Your data is stored privately. No sharing with employers or third parties. Only you have access to your productivity records." delay={240} />
          </div>
        </div>
      </section>

      {/* ══ HOW IT WORKS ════════════════════════════════════════════════════ */}
      <section id="how-it-works" className="py-20 md:py-28 px-4" style={{ background: "linear-gradient(160deg, #f0f7ff 0%, #f8fafc 100%)" }}>
        <div className="max-w-7xl mx-auto">
          <FadeIn>
            <div className="text-center mb-16">
              <span className="text-green-600 text-sm font-semibold uppercase tracking-widest">Simple by Design</span>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mt-2 mb-4 tracking-tight">
                Up and Running in 3 Easy Steps
              </h2>
              <p className="text-slate-500 text-lg max-w-xl mx-auto">
                No complicated setup. Start tracking in seconds.
              </p>
            </div>
          </FadeIn>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 relative">
            {/* Connector line */}
            <div className="hidden md:block absolute top-14 left-1/3 right-1/3 h-0.5 bg-gradient-to-r from-blue-200 via-teal-200 to-green-200 z-0" />

            {[
              {
                step: "01",
                icon: Settings,
                title: "Set Up Your Profile",
                desc: "Enter your $/wRVU rate, tax rate, and your six most common visit codes for your Quick-Add Panel.",
                color: "#3b82f6",
                bg: "#eff6ff",
              },
              {
                step: "02",
                icon: Zap,
                title: "Log Visits Instantly",
                desc: "During your shift, just tap '+' next to the CPT code for each patient. One tap = one visit tracked. That's it.",
                color: "#14b8a6",
                bg: "#f0fdfa",
              },
              {
                step: "03",
                icon: TrendingUp,
                title: "See Your Earnings Grow",
                desc: <>Watch your wRVU total and earnings update in real time at the bottom of the screen. The tracker <span style={{whiteSpace:"nowrap"}}>auto-saves</span> as you go.</>,
                color: "#22c55e",
                bg: "#f0fdf4",
              },
            ].map(({ step, icon: Icon, title, desc, color, bg }, i) => (
              <FadeIn key={step} delay={i * 120}>
                <div className="relative z-10 flex flex-col items-center text-center bg-white rounded-2xl p-8 border border-slate-100 shadow-sm hover:shadow-md transition-all duration-300">
                  <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-5 shadow-sm" style={{ background: bg }}>
                    <Icon size={26} style={{ color }} />
                  </div>
                  <div
                    className="absolute -top-3 -right-3 w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold shadow-md"
                    style={{ background: color }}
                  >
                    {step}
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-3">{title}</h3>
                  <p className="text-slate-500 text-sm leading-relaxed">{desc}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ══ PRICING ══════════════════════════════════════════════════════════ */}
      <section id="pricing" className="py-20 md:py-28 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <FadeIn>
            <div className="text-center mb-14">
              <span className="text-green-600 text-sm font-semibold uppercase tracking-widest">Simple Pricing</span>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mt-2 mb-4 tracking-tight">
                One Plan. No Surprises.
              </h2>
              <p className="text-slate-500 text-lg max-w-xl mx-auto">
                Everything included for the price of your morning coffee.
              </p>
            </div>
          </FadeIn>

          <div className="flex flex-col lg:flex-row gap-10 items-stretch justify-center">

            {/* Pricing card */}
            <FadeIn className="w-full max-w-md">
              <div
                className="relative bg-white rounded-3xl p-8 md:p-10 overflow-hidden h-full flex flex-col"
                style={{ border: "2px solid #dbeafe", boxShadow: "0 20px 60px rgba(37,99,235,0.12)" }}
              >
                {/* Top gradient strip */}
                <div className="absolute top-0 left-0 right-0 h-1.5 rounded-t-3xl" style={{ background: "linear-gradient(90deg, #2563eb, #0ea5e9, #14b8a6)" }} />

                <div className="flex items-start justify-between mb-6">
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-widest text-blue-600 mb-2">Monthly Plan</div>
                    <div className="flex items-end gap-1">
                      <span className="text-5xl font-extrabold text-slate-900">$9.99</span>
                      <span className="text-slate-500 mb-2 text-base">/month</span>
                    </div>
                  </div>
                  <div className="bg-blue-50 rounded-xl p-3">
                    <Activity size={24} className="text-blue-600" />
                  </div>
                </div>

                <ul className="flex flex-col justify-between mb-8 flex-1">
                  {[
                    "Unlimited shift tracking",
                    "Quick-Add visits (6 CPT codes)",
                    "Real-time wRVU & earnings calculator",
                    "Monthly calendar overview",
                    "Full shift history",
                    "CPT Reference panel",
                    "CSV / Excel data export",
                    "Custom $/wRVU & tax rate settings",
                    "Private & secure — your data stays yours",
                  ].map((item) => (
                    <li key={item} className="flex items-center gap-4 text-slate-600 text-sm">
                      <CheckCircle size={22} className="text-green-500 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>

                <a
                  href={`${APP_URL}/register`}
                  
                  
                  data-testid="pricing-cta-btn"
                  className="flex items-center justify-center gap-2 w-full text-white font-semibold rounded-2xl py-4 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-xl text-base"
                  style={{ background: "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)", boxShadow: "0 4px 20px rgba(37,99,235,0.30)" }}
                >
                  Get Started — $9.99/month
                  <ChevronRight size={18} />
                </a>

                <p className="text-center text-xs text-slate-400 mt-4">
                  Cancel anytime. No contracts. Billed monthly.
                </p>
              </div>
            </FadeIn>

            {/* Right column: Annual Plan + Latte Test stacked */}
            <FadeIn className="w-full max-w-md flex flex-col gap-5" delay={150}>

              {/* Annual plan — styled like the Monthly Plan card */}
              <div
                className="relative bg-white rounded-3xl p-7 flex-1 flex flex-col overflow-hidden"
                style={{ border: "2px solid #bbf7d0", boxShadow: "0 20px 60px rgba(22,163,74,0.10)" }}
              >
                {/* Top green gradient strip */}
                <div className="absolute top-0 left-0 right-0 h-1.5 rounded-t-3xl" style={{ background: "linear-gradient(90deg, #16a34a, #22c55e, #4ade80)" }} />

                <div className="flex items-start justify-between mb-4">
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-widest text-green-600 mb-2">Annual Plan</div>
                    <div className="flex items-end gap-1">
                      <span className="text-5xl font-extrabold text-slate-900">$99</span>
                      <span className="text-slate-500 mb-2 text-base">/year</span>
                    </div>
                  </div>
                  <div className="bg-green-50 rounded-xl p-3">
                    <Calendar size={24} className="text-green-600" />
                  </div>
                </div>

                <p className="flex items-center gap-2 text-slate-600 text-sm leading-relaxed mb-5 flex-1">
                  <CheckCircle size={16} className="text-green-500 flex-shrink-0" />
                  Same full access, better value.
                </p>

                <a
                  href={`${APP_URL}/register`}
                  
                  
                  data-testid="annual-cta-btn"
                  className="flex items-center justify-center gap-2 w-full text-white font-semibold rounded-2xl py-4 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-xl text-base"
                  style={{ background: "linear-gradient(135deg, #16a34a 0%, #15803d 100%)", boxShadow: "0 4px 20px rgba(22,163,74,0.30)" }}
                >
                  Get Annual Access — $99/year
                  <ChevronRight size={18} />
                </a>
                <p className="text-center text-xs text-slate-400 mt-4">
                  Billed once. Auto-renews annually.
                </p>
              </div>

              {/* ROI Value Proposition */}
              <div className="relative rounded-3xl p-7 flex-1 flex flex-col overflow-hidden" style={{ background: "linear-gradient(135deg, #0f172a 0%, #1e3a5f 100%)", border: "2px solid #1e40af" }}>

                <div className="flex items-center gap-3 mb-4">
                  <div className="bg-blue-500/20 rounded-xl p-2.5 flex-shrink-0">
                    <DollarSign size={22} className="text-blue-400" />
                  </div>
                  <div>
                    <h3 className="text-xl font-extrabold text-white leading-tight">
                      Make More Money.{" "}
                      <span style={{ background: "linear-gradient(135deg, #2563eb 0%, #0ea5e9 100%)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", textDecoration: "underline", textDecorationColor: "#2563eb", textDecorationThickness: "2px", textUnderlineOffset: "3px" }}>
                        BOTTOMLINE.
                      </span>
                    </h3>
                  </div>
                </div>

                <p className="text-slate-300 text-sm leading-relaxed mb-5">
                  Catching <strong className="text-white">just ONE missed patient's productivity</strong> alone pays for an <strong className="text-white">entire year's access.</strong>
                  <br /><em>— Save thousands annually.</em>
                </p>

                <div className="space-y-3">
                  {[
                    { label: "1 missed Level 5 visit", value: "~$140 lost", negative: true },
                    { label: "Annual plan cost", value: "$99/year", negative: false },
                  ].map(({ label, value, negative }) => (
                    <div
                      key={label}
                      className="flex items-center justify-between rounded-xl px-4 py-3"
                      style={{
                        background: "rgba(255,255,255,0.06)",
                        border: "1px solid rgba(255,255,255,0.08)"
                      }}
                    >
                      <span className="text-sm font-medium text-slate-300">{label}</span>
                      <span className={`text-sm font-bold ${negative ? "text-red-400" : "text-green-400"}`}>{value}</span>
                    </div>
                  ))}
                </div>
              </div>

            </FadeIn>
          </div>
        </div>
      </section>

      {/* ══ TESTIMONIALS ════════════════════════════════════════════════════ */}
      <section className="py-20 md:py-24 px-4" style={{ background: "linear-gradient(160deg, #f0f7ff 0%, #f8fafc 100%)" }}>
        <div className="max-w-7xl mx-auto">
          <FadeIn>
            <div className="text-center mb-12">
              <span className="text-blue-600 text-sm font-semibold uppercase tracking-widest">Testimonials</span>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mt-2 mb-3 tracking-tight">
                Trusted by Providers Who Know Their Worth
              </h2>
            </div>
          </FadeIn>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                quote: "I used to wait for my monthly paycheck statement to know what I earned. Now I know my wRVU total before I even leave the building. This app is a game-changer for any productivity-based provider.",
                name: "Dr. M.A.",
                role: "Emergency Medicine Physician",
                stars: 5,
              },
              {
                quote: "The Quick-Add feature is brilliant. Between patients in a busy Urgent Care, the last thing I want to do is type in a bunch of codes. One tap per patient and I'm done. So simple.",
                name: "R.P., PA-C",
                role: "Urgent Care Provider",
                stars: 5,
              },
              {
                quote: "Finally something designed for doctors, not accountants. I can verify my paycheck against my own records now. And I can download my data right on an Excel sheet!",
                name: "S.K., APRN",
                role: "Primary Care Nurse Practitioner",
                stars: 5,
              },
            ].map(({ quote, name, role, stars }, i) => (
              <FadeIn key={name} delay={i * 100}>
                <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm hover:shadow-md transition-all duration-300 h-full flex flex-col">
                  <div className="flex gap-0.5 mb-4">
                    {Array.from({ length: stars }).map((_, j) => (
                      <Star key={j} size={14} className="text-amber-400 fill-amber-400" />
                    ))}
                  </div>
                  <p className="text-slate-600 text-sm leading-relaxed flex-1 mb-5 italic">"{quote}"</p>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-400 to-teal-500 flex items-center justify-center text-white text-sm font-bold flex-shrink-0">
                      {name[0]}
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-slate-800">{name}</div>
                      <div className="text-xs text-slate-400">{role}</div>
                    </div>
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ══ FINAL CTA ═══════════════════════════════════════════════════════ */}
      <section className="py-20 md:py-28 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <FadeIn>
            <div
              className="rounded-3xl p-10 md:p-14"
              style={{ background: "linear-gradient(135deg, #1e3a8a 0%, #1d4ed8 50%, #0369a1 100%)", boxShadow: "0 30px 80px rgba(29,78,216,0.25)" }}
            >
              <div className="flex justify-center mb-6">
                <img
                  src="/rvu-logo.png"
                  alt="Doc RVU Tracker Logo"
                  style={{ width: 72, height: 72, borderRadius: "18px", objectFit: "cover", boxShadow: "0 4px 20px rgba(22,163,74,0.40)" }}
                />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 tracking-tight">
                Start Tracking Your RVUs Today
              </h2>
              <p className="text-blue-100 text-lg mb-8 leading-relaxed max-w-xl mx-auto">
                Join the providers in Urgent Care, ER, and Primary Care who are taking control of their income - One Click at a Time!
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <a
                  href={`${APP_URL}/register`}
                  
                  
                  data-testid="final-cta-register"
                  className="flex flex-col items-center justify-center gap-0.5 text-blue-700 font-semibold rounded-full px-8 py-3 bg-white transition-all duration-200 hover:bg-blue-50 hover:-translate-y-0.5 hover:shadow-xl"
                >
                  <span className="flex items-center gap-2 text-lg">Get Started for Free <ChevronRight size={20} /></span>
                  <span className="text-xs text-blue-400 font-normal">Start your free trial.</span>
                </a>
                <a
                  href={`${APP_URL}/login`}
                  
                  
                  data-testid="final-cta-login"
                  className="flex items-center justify-center gap-2 text-white font-medium rounded-full px-8 py-3.5 border border-white/30 hover:bg-white/10 transition-all duration-200"
                >
                  Already have an account? Log In
                </a>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ══ FOOTER ═══════════════════════════════════════════════════════════ */}
      {/* ── FREE TOOL CALLOUT ─────────────────────────────────────────────── */}
      <section className="py-10 px-4 bg-slate-50 border-t border-slate-200">
        <div className="max-w-3xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div>
            <p className="font-bold text-slate-800 text-base">Not ready to subscribe?</p>
            <p className="text-slate-500 text-sm">Try our free wRVU calculator — no account required. Instant CPT code lookup with real CMS wRVU values.</p>
          </div>
          <a
            href="/free-rvu-calculator"
            className="flex-shrink-0 flex items-center gap-2 text-sm font-bold text-white rounded-full px-6 py-2.5 transition-all hover:-translate-y-px hover:shadow-lg"
            style={{ background: "linear-gradient(135deg, #2563eb 0%, #0ea5e9 100%)" }}
          >
            Free wRVU Calculator →
          </a>
        </div>
      </section>

      <footer className="bg-slate-900 text-slate-400 py-12 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <div className="flex items-center gap-2.5 mb-2">
                <img
                  src="/rvu-logo.png"
                  alt="Doc RVU Tracker Logo"
                  style={{ width: 32, height: 32, borderRadius: "8px", objectFit: "cover", boxShadow: "0 2px 8px rgba(22,163,74,0.30)" }}
                />
                <span className="text-white font-semibold text-sm">Doc RVU Tracker</span>
              </div>
              <p className="text-xs text-slate-500 max-w-xs">
                The One-Click tracker built for providers by providers.
              </p>
              <p className="text-xs text-slate-600">© {new Date().getFullYear()} Doc RVU Tracker. All rights reserved.</p>
            </div>
            <div className="flex flex-col items-center md:items-end gap-1">
              <div className="flex gap-5">
                <a href={`${APP_URL}/login`}   className="text-sm hover:text-white transition-colors">Log In</a>
                <a href={`${APP_URL}/register`}   className="text-sm hover:text-white transition-colors">Sign Up</a>
              </div>
              <p className="text-sm font-medium text-slate-300">DocRVUTracker@gmail.com</p>
              <p className="text-xs text-slate-400 mt-0.5">For Customer Service and Inquiries, email above</p>
            </div>
          </div>
        </div>
      </footer>

      {/* ── Global CSS animations ─────────────────────────────────────────── */}
      <style>{`
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(24px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes fadeDown {
          from { opacity: 0; transform: translateY(-12px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        html { scroll-behavior: smooth; }
      `}</style>
    </div>
    {lightbox && <Lightbox src={lightbox.src} alt={lightbox.alt} onClose={() => setLightbox(null)} />}
    </LightboxContext.Provider>
  );
}
